import { useEffect, useRef, useState } from "react";
import { Paperclip, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  PRIORITIES,
  PRIORITY_LABEL,
  SEVERITIES,
  STATUS_LABEL,
  TICKET_TYPES,
} from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_FILES = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const createEmptyForm = () => ({
  organization_id: "",
  title: "",
  description: "",
  ticket_type: "incident",
  suggested_priority: "P3",
  severity: "moderate",
  business_impact: "",
  affected_url: "",
});

export default function CreateTicketDialog({
  open,
  onOpenChange,
  onCreated,
}) {
  const [organizations, setOrganizations] = useState([]);
  const [form, setForm] = useState(createEmptyForm);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    api.get("/organizations")
      .then((response) => {
        const organizationItems = response.data || [];

        setOrganizations(organizationItems);

        setForm((current) => ({
          ...current,
          organization_id:
            current.organization_id ||
            (organizationItems.length === 1
              ? organizationItems[0].id
              : ""),
        }));
      })
      .catch((error) => {
        toast.error(formatApiError(error));
      });
  }, [open]);

  const setField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const resetDialog = () => {
    setForm(createEmptyForm());
    setFiles([]);
    setSubmitting(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const closeDialog = () => {
    resetDialog();
    onOpenChange(false);
  };

  const addFiles = (selectedFiles) => {
    const incoming = Array.from(selectedFiles || []);

    if (!incoming.length) return;

    const oversizedFile = incoming.find(
      (file) => file.size > MAX_FILE_SIZE,
    );

    if (oversizedFile) {
      toast.error(
        `${oversizedFile.name} exceeds the maximum file size of 20 MB`,
      );
      return;
    }

    const combined = [...files, ...incoming];

    if (combined.length > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files`);
      return;
    }

    const uniqueFiles = combined.filter(
      (file, index, allFiles) =>
        index ===
        allFiles.findIndex(
          (candidate) =>
            candidate.name === file.name &&
            candidate.size === file.size &&
            candidate.lastModified === file.lastModified,
        ),
    );

    setFiles(uniqueFiles);
  };

  const removeFile = (indexToRemove) => {
    setFiles((current) =>
      current.filter((_, index) => index !== indexToRemove),
    );
  };

  const uploadAttachments = async (ticketId) => {
    const uploaded = [];
    const failed = [];

    for (const file of files) {
      const uploadData = new FormData();
      uploadData.append("file", file);

      try {
        await api.post(
          `/tickets/${ticketId}/attachments`,
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        uploaded.push(file.name);
      } catch (error) {
        failed.push({
          name: file.name,
          message: formatApiError(error),
        });
      }
    }

    return { uploaded, failed };
  };

  const validate = (saveAsDraft = false) => {
    if (organizations.length > 1 && !form.organization_id) {
      toast.error("Please select a client");
      return false;
    }

    if (!form.title.trim()) {
      toast.error("Title is required");
      return false;
    }

    if (form.title.trim().length > 100) {
      toast.error("Title cannot exceed 100 characters");
      return false;
    }

    // Drafts require only a title. The remaining mandatory
    // fields are checked when the ticket is finally submitted.
    if (saveAsDraft) {
      return true;
    }

    if (!form.ticket_type) {
      toast.error("Issue type is required");
      return false;
    }

    if (!form.suggested_priority) {
      toast.error("Priority is required");
      return false;
    }

    if (!form.severity) {
      toast.error("Severity is required");
      return false;
    }

    if (!form.description.trim()) {
      toast.error("Description is required");
      return false;
    }

    return true;
  };

  const submit = async (saveAsDraft = false) => {
    if (!validate(saveAsDraft)) return;

    setSubmitting(true);

    try {
      const payload = {
        organization_id: form.organization_id || null,
        title: form.title.trim(),
        description: form.description.trim() || null,
        ticket_type: form.ticket_type,
        suggested_priority: form.suggested_priority,
        severity: form.severity,
        business_impact: form.business_impact.trim() || null,
        affected_url: form.affected_url.trim() || null,
        save_as_draft: saveAsDraft,
      };

      const { data: ticket } = await api.post("/tickets", payload);

      let attachmentResult = {
        uploaded: [],
        failed: [],
      };

      if (files.length) {
        attachmentResult = await uploadAttachments(ticket.id);
      }

      if (attachmentResult.failed.length) {
        toast.warning(
          `Ticket ${ticket.ticket_number} was created, but ${attachmentResult.failed.length} attachment(s) could not be uploaded`,
        );
      } else {
        toast.success(
          saveAsDraft
            ? `Ticket ${ticket.ticket_number} saved as draft`
            : `Ticket ${ticket.ticket_number} submitted successfully`,
        );
      }

      resetDialog();
      onOpenChange(false);
      onCreated?.(ticket);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          resetDialog();
        }

        onOpenChange(value);
      }}
    >
      <DialogContent
        data-testid="create-ticket-dialog"
        className="left-0 top-0 h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none"
      >
        <div className="flex h-full min-h-0 flex-col bg-background">
          <div className="shrink-0 border-b border-border bg-card/90 px-5 py-4 pr-14 backdrop-blur md:px-8">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold md:text-3xl">
                Raise a New Support Ticket
              </DialogTitle>

              <DialogDescription>
                Describe the issue in your own words and attach screenshots
                or supporting files where helpful.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="md-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1">
            <div className="mx-auto w-full max-w-6xl space-y-6 px-5 py-6 pr-7 md:px-8 md:py-8 md:pr-10">
              <section className="rounded-xl border border-border bg-card/60 p-5 md:p-6">
                <div className="mb-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Issue details
                  </div>
                  <h2 className="mt-1 font-display text-xl font-bold">
                    Tell Us What Happened
                  </h2>

                  <p className="mt-1 text-xs text-muted-foreground">
                    Fields marked with
                    <span className="mx-1 font-semibold text-rose-500">*</span>
                    are required when submitting the ticket.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {organizations.length > 1 && (
                    <div className="md:col-span-2">
                      <Label>Client <span className="text-rose-500">*</span></Label>

                      <Select
                        value={form.organization_id}
                        onValueChange={(value) =>
                          setField("organization_id", value)
                        }
                      >
                        <SelectTrigger data-testid="ticket-form-organization">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>

                        <SelectContent>
                          {organizations.map((organization) => (
                            <SelectItem
                              key={organization.id}
                              value={organization.id}
                            >
                              {organization.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <Label htmlFor="title">Title <span className="text-rose-500">*</span></Label>
                      <span className="text-[11px] text-muted-foreground">
                        {form.title.length}/100
                      </span>
                    </div>

                    <Input
                      id="title"
                      data-testid="ticket-form-title"
                      required
                      aria-required="true"
                      maxLength={100}
                      value={form.title}
                      onChange={(event) =>
                        setField("title", event.target.value)
                      }
                      placeholder="Briefly summarise the issue"
                    />
                  </div>

                  <div>
                    <Label>Issue Type <span className="text-rose-500">*</span></Label>

                    <Select
                      value={form.ticket_type}
                      onValueChange={(value) =>
                        setField("ticket_type", value)
                      }
                    >
                      <SelectTrigger data-testid="ticket-form-type">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        {TICKET_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {STATUS_LABEL[type] || type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Priority <span className="text-rose-500">*</span></Label>

                    <Select
                      value={form.suggested_priority}
                      onValueChange={(value) =>
                        setField("suggested_priority", value)
                      }
                    >
                      <SelectTrigger data-testid="ticket-form-priority">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        {PRIORITIES.map((priority) => (
                          <SelectItem key={priority} value={priority}>
                            {PRIORITY_LABEL[priority]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Severity <span className="text-rose-500">*</span></Label>

                    <Select
                      value={form.severity}
                      onValueChange={(value) =>
                        setField("severity", value)
                      }
                    >
                      <SelectTrigger data-testid="ticket-form-severity">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        {SEVERITIES.map((severity) => (
                          <SelectItem key={severity} value={severity}>
                            {severity.charAt(0).toUpperCase() +
                              severity.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="hidden md:block" />

                  <div className="md:col-span-2">
                    <Label htmlFor="description">Description <span className="text-rose-500">*</span></Label>

                    <Textarea
                      id="description"
                      data-testid="ticket-form-description"
                      required
                      aria-required="true"
                      value={form.description}
                      onChange={(event) =>
                        setField("description", event.target.value)
                      }
                      rows={7}
                      placeholder="Describe what you were trying to do, what happened, and any error message you saw."
                      className="resize-y"
                    />

                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Include the error message or visible behaviour. You can
                      attach screenshots below.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="business-impact">
                      Business impact{" "}
                      <span className="font-normal text-muted-foreground">
                        (optional)
                      </span>
                    </Label>

                    <Textarea
                      id="business-impact"
                      data-testid="ticket-form-impact"
                      value={form.business_impact}
                      onChange={(event) =>
                        setField("business_impact", event.target.value)
                      }
                      rows={3}
                      placeholder="Example: The Finance team cannot generate invoices for customers."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="affected-url">
                      Affected URL{" "}
                      <span className="font-normal text-muted-foreground">
                        (optional)
                      </span>
                    </Label>

                    <Input
                      id="affected-url"
                      data-testid="ticket-form-url"
                      value={form.affected_url}
                      onChange={(event) =>
                        setField("affected_url", event.target.value)
                      }
                      placeholder="https://example.com/page"
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-border bg-card/60 p-5 md:p-6">
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Supporting files
                  </div>
                  <h2 className="mt-1 font-display text-xl font-bold">
                    Attach Screenshots or Documents
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Upload up to {MAX_FILES} files, with a maximum size of
                    20 MB per file.
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.gif,.webp,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.log,.zip,.mp4,.mov,.webm"
                  onChange={(event) => addFiles(event.target.files)}
                  data-testid="ticket-form-file-input"
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-border bg-background/40 px-6 py-10 text-center transition-colors hover:border-[#d4af37]/50 hover:bg-[#d4af37]/5"
                  data-testid="ticket-form-file-picker"
                >
                  <UploadCloud className="mb-3 h-8 w-8 text-[#e5c158]" />
                  <span className="font-semibold">
                    Browse and select files
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground">
                    Screenshots, PDF, Word, Excel, ZIP, video, text and log
                    files
                  </span>
                </button>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/50 px-3 py-2"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <Paperclip className="h-4 w-4 shrink-0 text-[#e5c158]" />

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                              {file.name}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-400"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="rounded-lg border border-[#d4af37]/20 bg-[#d4af37]/5 px-4 py-3 text-xs text-muted-foreground">
                The environment will automatically be recorded as{" "}
                <span className="font-semibold text-foreground">
                  Production
                </span>
                . Client, user, date, ticket number and SLA details are added
                automatically.
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-border bg-card/95 px-5 py-4 backdrop-blur md:px-8">
            <div className="mx-auto flex w-full max-w-6xl flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button
                variant="outline"
                data-testid="ticket-form-cancel"
                onClick={closeDialog}
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                variant="outline"
                data-testid="ticket-form-draft"
                onClick={() => submit(true)}
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save as draft"}
              </Button>

              <Button
                data-testid="ticket-form-submit"
                onClick={() => submit(false)}
                disabled={submitting}
                className="bg-gradient-to-b from-[#e5c158] to-[#d4af37] text-black hover:from-[#f0cf6d] hover:to-[#e5c158]"
              >
                {submitting ? "Submitting..." : "Submit ticket"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
